import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { supportedLanguages, type SupportedLanguage } from '@/i18n/config';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateLanguageMutation = useMutation({
    mutationFn: async (language: SupportedLanguage) => {
      return apiRequest('PATCH', '/api/user/language', { language });
    },
    onSuccess: (_data, langCode) => {
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      toast({
        title: t('settings.saved'),
        description: supportedLanguages.find(l => l.code === langCode)?.nativeName,
      });
    },
    onError: () => {
      toast({
        title: t('settings.saveError'),
        description: t('settings.saveErrorDesc'),
        variant: 'destructive',
      });
    }
  });

  const handleLanguageChange = (langCode: SupportedLanguage) => {
    if (langCode === i18n.resolvedLanguage) return;
    i18n.changeLanguage(langCode);
    document.documentElement.lang = langCode;
    localStorage.setItem('healthwallet-language', langCode);

    if (user) {
      updateLanguageMutation.mutate(langCode);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="button-language-switcher"
        >
          <Globe className="h-5 w-5" />
          <span className="sr-only">{t('common.language')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
            data-testid={`menu-item-language-${lang.code}`}
            className={i18n.resolvedLanguage === lang.code ? 'bg-accent' : ''}
          >
            <span className="font-medium">{lang.nativeName}</span>
            <span className="ml-2 text-muted-foreground text-sm">({lang.name})</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
